/**
 * AP-35 Detailblatt 5 5.3a (Nachtrag 13) — **Dialog „Preisanfrage / Bestellung erzeugen"** im
 * Einkaufs-Cockpit. Ein Dialog für beide Belegarten (N13-1), aufgebaut wie der Dialog
 * „Preisanfrage erzeugen" der Kalkulation (AP-37 Detailblatt O 6.1): links Belegart, Empfänger
 * und Aufbau, rechts die Vorschau je Beleg — jede Änderung ruft `dryRun`.
 *
 * - **Preisanfrage:** beliebig viele Empfänger; jeder bekommt alle gewählten Zeilen (N13-4).
 * - **Bestellung:** genau **ein** Lieferant (N13-2).
 *
 * Einzelne Zeilen nimmt das ✕ in der Vorschau je Empfänger heraus. „Erzeugen" legt die Belege
 * als Entwurf an; die Seite öffnet danach den Beleg (N13-3).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, FileQuestion, Loader2, Paperclip, Search, Star, X } from 'lucide-react'
import { geldText } from '@symphenia/shared-types'
import { Modal } from '@symphenia/ui-components'
import { apiErrorText } from '@/lib/api-error'
import { fieldLabel, groupLabel, inputBase, selectBase } from '@/lib/form-styles'
import { useTerminology } from '@/lib/useTerminology'
import {
  createPurchaseDocuments,
  searchSuppliers,
  type CreateDocumentsParams,
  type CreateDocumentsResult,
  type PurchaseDocumentKind,
  type PurchaseSuggestion,
} from '../api/project-bom.api'

const mengeText = (x: number) => x.toLocaleString('de-DE', { maximumFractionDigits: 3 })
const groesse = (b: number) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB` : `${Math.max(1, Math.round(b / 1024))} kB`)
const mehrzahl = (anzahl: number, eins: string, viele: string) => `${anzahl} ${anzahl === 1 ? eins : viele}`

const kaestchen = 'mt-0.5 h-4 w-4 shrink-0 accent-[hsl(var(--primary))]'
const option = 'flex cursor-pointer items-start gap-2'
const feld = 'py-1.5 text-sm'

/** Warum eine Zeile in keinen Beleg kommt — die Freigabe heißt je Belegart anders. */
function grundText(reason: string, art: PurchaseDocumentKind, detail?: string): string {
  switch (reason) {
    case 'BOM_APPROVAL_REQUIRED':
      return art === 'INQUIRY' ? 'Anfragefreigabe fehlt' : 'Bestellfreigabe fehlt'
    case 'BOM_NO_SUPPLIER':
      return 'ohne Lieferant'
    case 'BOM_ALREADY_ORDERED':
      return 'schon bestellt'
    case 'BOM_ALREADY_IN_DOCUMENT':
      return `steht schon in ${detail ?? 'einem Beleg'}`
    default:
      return reason
  }
}

type Lieferant = { id: string; name: string }

export function PurchaseDocumentsDialog({
  lineIds,
  onClose,
  onDone,
}: {
  lineIds: string[]
  onClose: () => void
  /** nach dem Anlegen — die Seite öffnet den Beleg oder zeigt die Verweise */
  onDone: (ergebnis: CreateDocumentsResult, art: PurchaseDocumentKind) => void
}) {
  const terms = useTerminology()
  const [art, setArt] = useState<PurchaseDocumentKind>('INQUIRY')
  // Bis die Vorschläge da sind, rechnet die Vorschau je Lieferant der Zeilen (wie die API ohne Empfänger).
  const [vorschlaege, setVorschlaege] = useState<PurchaseSuggestion[] | null>(null)
  const [hinzugefuegt, setHinzugefuegt] = useState<Lieferant[]>([])
  const [gewaehlt, setGewaehlt] = useState<Lieferant[]>([])
  const [entfernt, setEntfernt] = useState<{ supplierId: string; lineId: string }[]>([])
  const [teilung, setTeilung] = useState<'PER_SUPPLIER' | 'PER_SUPPLIER_AND_PROJECT'>('PER_SUPPLIER')
  const [adresse, setAdresse] = useState<'NONE' | 'PROJECT_SITE'>('NONE')
  const [withFiles, setWithFiles] = useState(true)
  const [abgehakt, setAbgehakt] = useState<Set<string>>(new Set())
  const [vorschau, setVorschau] = useState<CreateDocumentsResult | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [suche, setSuche] = useState('')
  const [treffer, setTreffer] = useState<Lieferant[]>([])
  const zaehler = useRef(0)

  const bestellung = art === 'PURCHASE_ORDER'
  const bereit = vorschlaege !== null
  const params = useMemo<CreateDocumentsParams>(
    () => ({
      lineIds,
      kind: art,
      ...(bereit ? { supplierIds: gewaehlt.map((g) => g.id), excludedLines: entfernt } : {}),
      split: teilung,
      deliveryAddress: adresse,
      withFiles,
      excludedFileIds: [...abgehakt],
    }),
    [lineIds, art, bereit, gewaehlt, entfernt, teilung, adresse, withFiles, abgehakt],
  )
  const wahlFehlt = bereit && gewaehlt.length === 0

  /** Vorgabe der Empfänger: die Lieferanten der Zeilen — bei einer Bestellung nur, wenn es genau einer ist. */
  const vorgabe = (liste: PurchaseSuggestion[], fuer: PurchaseDocumentKind): Lieferant[] => {
    const anZeile = liste.filter((s) => s.lineCount > 0).map((s) => ({ id: s.id, name: s.name }))
    return fuer === 'INQUIRY' || anZeile.length === 1 ? anZeile : []
  }

  // Vorschau bei jeder Änderung, die letzte Antwort gewinnt.
  useEffect(() => {
    if (wahlFehlt) {
      zaehler.current++
      setVorschau(null)
      setLaedt(false)
      return
    }
    const nr = ++zaehler.current
    const t = setTimeout(() => {
      setLaedt(true)
      createPurchaseDocuments({ ...params, dryRun: true })
        .then((r) => {
          if (nr !== zaehler.current) return
          setVorschau(r)
          setFehler(null)
          // Erste Antwort: Vorschläge merken, die Lieferanten der Zeilen sind gewählt.
          if (vorschlaege === null) {
            setVorschlaege(r.suggestions ?? [])
            setGewaehlt(vorgabe(r.suggestions ?? [], art))
          }
        })
        .catch((e) => nr === zaehler.current && setFehler(apiErrorText(e, 'Die Vorschau ließ sich nicht rechnen.')))
        .finally(() => nr === zaehler.current && setLaedt(false))
    }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, wahlFehlt])

  useEffect(() => {
    if (suche.trim().length < 2) return setTreffer([])
    const t = setTimeout(() => {
      searchSuppliers(suche.trim())
        .then(setTreffer)
        .catch(() => setTreffer([]))
    }, 250)
    return () => clearTimeout(t)
  }, [suche])

  const artWaehlen = (neu: PurchaseDocumentKind) => {
    if (neu === art) return
    setArt(neu)
    // Eine Bestellung hat genau einen Lieferanten: Mehrere gewählte fallen weg, die Wahl ist neu zu treffen.
    if (neu === 'PURCHASE_ORDER' && gewaehlt.length > 1) setGewaehlt([])
    if (neu === 'INQUIRY' && gewaehlt.length === 0) setGewaehlt(vorgabe(vorschlaege ?? [], 'INQUIRY'))
  }

  const istGewaehlt = (id: string) => gewaehlt.some((g) => g.id === id)
  const waehlen = (e: Lieferant) =>
    setGewaehlt((alt) => (bestellung ? [e] : alt.some((g) => g.id === e.id) ? alt.filter((g) => g.id !== e.id) : [...alt, e]))
  const entferntBei = (supplierId: string) => entfernt.filter((x) => x.supplierId === supplierId).length
  const zurueckholen = (supplierId: string) => setEntfernt((alt) => alt.filter((x) => x.supplierId !== supplierId))
  const n = lineIds.length
  const herkunft = (s: PurchaseSuggestion) =>
    s.lineCount > 0 ? `an der Stücklistenzeile hinterlegt · ${s.lineCount} von ${n} Zeilen` : `im Artikelstamm als Lieferant · ${s.articleCount} von ${n} Zeilen`
  const empfaenger = [
    ...(vorschlaege ?? []).map((s) => ({ id: s.id, name: s.name, hinweis: herkunft(s), vorzug: s.preferred })),
    ...hinzugefuegt.filter((h) => !vorschlaege?.some((s) => s.id === h.id)).map((h) => ({ id: h.id, name: h.name, hinweis: 'selbst hinzugefügt', vorzug: false })),
  ]
  const weitereTreffer = treffer.filter((t) => !empfaenger.some((e) => e.id === t.id))

  const erzeugen = async () => {
    setLaedt(true)
    setFehler(null)
    try {
      onDone(await createPurchaseDocuments({ ...params, dryRun: false }), art)
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Belege ließen sich nicht anlegen.'))
      setLaedt(false)
    }
  }

  const belege = vorschau?.groups ?? []
  const zeilenGesamt = belege.reduce((summe, b) => summe + b.lines.length, 0)
  const zurueckgehalten = vorschau?.skipped ?? []
  const nichts = wahlFehlt || (vorschau !== null && belege.length === 0)
  const belegWort = bestellung ? (['Bestellung', 'Bestellungen'] as const) : (['Preisanfrage', 'Preisanfragen'] as const)

  return (
    <Modal
      isOpen
      onClose={onClose}
      scrollBody={false}
      className="h-[85vh] max-w-6xl"
      title={
        <span className="text-primary">
          Preisanfrage / Bestellung erzeugen
          <span className="block text-xs font-normal text-muted-foreground">
            {mehrzahl(n, 'Stücklistenzeile', 'Stücklistenzeilen')} gewählt — je Beleg ein Entwurf im Belegwesen; ein einzelner Beleg öffnet sich nach dem Erzeugen.
          </span>
        </span>
      }
      footer={
        <>
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted" onClick={onClose}>
            Abbrechen
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            disabled={laedt || !belege.length}
            onClick={() => void erzeugen()}
          >
            {belege.length > 1 ? `${belege.length} ${belegWort[1]} erzeugen` : `${belegWort[0]} erzeugen`}
          </button>
        </>
      }
    >
      <div className="grid min-h-0 flex-1 grid-rows-[auto_1fr] overflow-y-auto lg:grid-cols-[22rem_1fr] lg:grid-rows-1 lg:overflow-hidden">
        {/* Einstellungen — welcher Beleg, an wen, wie aufgebaut */}
        <aside className="space-y-6 border-b border-border bg-muted/20 p-5 text-sm lg:overflow-y-auto lg:border-b-0 lg:border-r">
          <section className="space-y-2">
            <h3 className={groupLabel}>Belegart</h3>
            <div className="grid grid-cols-2 gap-1 rounded-md border border-border bg-background p-1" role="radiogroup" aria-label="Belegart">
              {(
                [
                  ['INQUIRY', 'Preisanfrage'],
                  ['PURCHASE_ORDER', 'Bestellung'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={art === key}
                  className={`rounded px-3 py-1.5 text-sm ${art === key ? 'bg-primary font-medium text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  onClick={() => artWaehlen(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <h3 className={groupLabel}>{bestellung ? 'Lieferant' : 'Empfänger'}</h3>
            <p className="text-xs text-muted-foreground">
              {bestellung
                ? 'Eine Bestellung geht an genau einen Lieferanten — er bekommt alle gewählten Zeilen.'
                : 'Jeder angehakte Lieferant bekommt alle gewählten Zeilen.'}{' '}
              Einzelne Zeilen nimmst du rechts in der Vorschau mit ✕ heraus.
            </p>
            {!bereit && <p className="text-xs text-muted-foreground">Vorschläge werden geladen …</p>}
            {bereit && empfaenger.length === 0 && (
              <p className="text-xs text-muted-foreground">Keine Vorschläge — weder an den Stücklistenzeilen noch im Artikelstamm ist ein Lieferant hinterlegt.</p>
            )}
            {empfaenger.length > 0 && (
              <div className="divide-y divide-border overflow-hidden rounded-md border border-border bg-background">
                {empfaenger.map((e) => (
                  <label key={e.id} className={`${option} px-3 py-2 hover:bg-muted/40`}>
                    <input
                      type={bestellung ? 'radio' : 'checkbox'}
                      name={bestellung ? 'einkauf-lieferant' : undefined}
                      className={kaestchen}
                      checked={istGewaehlt(e.id)}
                      onChange={() => waehlen(e)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="truncate font-medium">{e.name}</span>
                        {e.vorzug && (
                          <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-primary/10 px-1.5 py-px text-[10px] font-medium text-primary" title="Vorzugslieferant">
                            <Star className="h-2.5 w-2.5" />
                            Vorzugslieferant
                          </span>
                        )}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {e.hinweis}
                        {istGewaehlt(e.id) && entferntBei(e.id) > 0 && (
                          <>
                            {' · '}
                            {mehrzahl(entferntBei(e.id), 'Zeile', 'Zeilen')} herausgenommen{' '}
                            <button type="button" className="text-primary hover:underline" onClick={() => zurueckholen(e.id)}>
                              wieder aufnehmen
                            </button>
                          </>
                        )}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}
            {bereit && (
              <div>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className={`${inputBase} ${feld} pl-8`}
                    placeholder={bestellung ? 'Anderen Lieferanten suchen …' : 'Weiteren Lieferanten hinzufügen …'}
                    value={suche}
                    onChange={(e) => setSuche(e.target.value)}
                  />
                </div>
                {suche.trim().length >= 2 && (
                  <div className="mt-1 max-h-40 overflow-auto rounded-md border border-border bg-background shadow-sm">
                    {weitereTreffer.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className="block w-full truncate px-3 py-1.5 text-left hover:bg-muted"
                        onClick={() => {
                          setHinzugefuegt((alt) => [...alt, { id: t.id, name: t.name }])
                          setGewaehlt((alt) => (bestellung ? [{ id: t.id, name: t.name }] : [...alt, { id: t.id, name: t.name }]))
                          setSuche('')
                        }}
                      >
                        {t.name}
                      </button>
                    ))}
                    {weitereTreffer.length === 0 && <p className="px-3 py-1.5 text-xs text-muted-foreground">Kein weiterer Lieferant gefunden.</p>}
                  </div>
                )}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h3 className={groupLabel}>Aufbau der Belege</h3>
            <div className="space-y-1.5">
              <label htmlFor="ek-aufteilen" className={fieldLabel}>
                Aufteilen
              </label>
              <select id="ek-aufteilen" className={`${selectBase} ${feld}`} value={teilung} onChange={(e) => setTeilung(e.target.value as typeof teilung)}>
                <option value="PER_SUPPLIER">ein Beleg je Lieferant</option>
                <option value="PER_SUPPLIER_AND_PROJECT">ein Beleg je Lieferant und {terms.project}</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label htmlFor="ek-lieferadresse" className={fieldLabel}>
                Lieferadresse
              </label>
              <select id="ek-lieferadresse" className={`${selectBase} ${feld}`} value={adresse} onChange={(e) => setAdresse(e.target.value as typeof adresse)}>
                <option value="NONE">Lieferung an uns</option>
                <option value="PROJECT_SITE">Lieferung an die Baustelle</option>
              </select>
            </div>
            <label className={`${option} pt-1`}>
              <input type="checkbox" className={kaestchen} checked={withFiles} onChange={(e) => setWithFiles(e.target.checked)} />
              <span>Dateien der Stücklistenzeilen mitsenden</span>
            </label>
          </section>
        </aside>

        {/* Vorschau */}
        <section className="space-y-3 p-5 lg:min-h-0 lg:overflow-y-auto">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className={groupLabel}>Vorschau</h3>
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              {laedt && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {vorschau &&
                [mehrzahl(belege.length, belegWort[0], belegWort[1]), mehrzahl(zeilenGesamt, 'Zeile', 'Zeilen'), zurueckgehalten.length ? `${zurueckgehalten.length} zurückgehalten` : '']
                  .filter(Boolean)
                  .join(' · ')}
            </span>
          </div>
          {fehler && <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}
          {laedt && !vorschau && !wahlFehlt && <p className="text-sm text-muted-foreground">Vorschau wird gerechnet …</p>}

          {nichts && (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-6 py-8 text-center text-sm text-muted-foreground">
              <FileQuestion className="h-8 w-8 opacity-60" />
              {wahlFehlt ? (
                <p>{bestellung ? 'Bitte links den Lieferanten wählen, bei dem bestellt wird.' : 'Bitte links mindestens einen Lieferanten anhaken.'}</p>
              ) : (
                <>
                  <p className="font-medium text-foreground">{bestellung ? 'Keine Zeile kann bestellt werden.' : 'Keine Zeile kann angefragt werden.'}</p>
                  {zurueckgehalten.length > 0 && <p>Warum, steht darunter bei den zurückgehaltenen Zeilen.</p>}
                </>
              )}
            </div>
          )}

          {belege.map((b) => (
            <article key={`${b.supplierId}-${b.lines[0]?.lineId ?? ''}`} className="overflow-hidden rounded-lg border border-border bg-background">
              <header className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 border-b border-border bg-muted/30 px-3 py-2">
                <div className="font-medium">{b.supplierName}</div>
                <div className="text-xs text-muted-foreground">
                  {b.projects.join(' · ')} · {mehrzahl(b.lines.length, 'Zeile', 'Zeilen')}
                  {bestellung && b.purchaseTotal !== undefined && ` · ${geldText(b.purchaseTotal)}`}
                </div>
                {/* AP-35 N17-6: „Ihr Zeichen" nennt das Angebot, aus dem die Preise kommen. */}
                {bestellung && b.customerReference && <div className="w-full text-xs text-muted-foreground">Ihr Zeichen: {b.customerReference}</div>}
              </header>
              <table className="w-full text-sm">
                <tbody>
                  {b.lines.map((l) => (
                    <tr key={l.lineId} className="group border-t border-border/60 align-top first:border-t-0">
                      <td className="w-24 py-1.5 pl-3 pr-2 text-right tabular-nums">{mengeText(l.quantity)}</td>
                      <td className="w-12 py-1.5 pr-2 text-muted-foreground">{l.unitCode}</td>
                      <td className="py-1.5">
                        {l.name}
                        {l.material && <span className="ml-1 text-xs text-muted-foreground">{l.material}</span>}
                        {l.files > 0 && (
                          <span className="ml-1 inline-flex items-center text-xs text-muted-foreground">
                            <Paperclip className="h-3 w-3" />
                            {l.files}
                          </span>
                        )}
                        <div className="text-xs text-muted-foreground">
                          {l.project} · {l.position}
                          {l.lineSupplier && <span className="ml-1 text-amber-700 dark:text-amber-300">· an der Zeile hinterlegt: {l.lineSupplier}</span>}
                          {/* N17-6, N17-7: Preis aus dem Angebot des Empfängers — oder warum nicht. */}
                          {bestellung && l.offerDocumentNumber && <span className="ml-1 text-emerald-700 dark:text-emerald-400">· Preis aus Angebot ({l.offerDocumentNumber})</span>}
                          {bestellung && l.offerUnitHint && (
                            <span className="ml-1 text-amber-700 dark:text-amber-300">· Angebot je {l.offerUnitHint} — erst an der Zeile übernehmen</span>
                          )}
                        </div>
                      </td>
                      <td className="w-8 py-1.5 pr-2 text-right">
                        <button
                          type="button"
                          className="rounded p-0.5 text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive group-hover:text-muted-foreground"
                          title={`Diese Zeile nicht bei ${b.supplierName} ${bestellung ? 'bestellen' : 'anfragen'}`}
                          aria-label={`Diese Zeile nicht bei ${b.supplierName} ${bestellung ? 'bestellen' : 'anfragen'}`}
                          onClick={() => setEntfernt((alt) => [...alt, { supplierId: b.supplierId, lineId: l.lineId }])}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {withFiles && b.files.length > 0 && (
                <div className="border-t border-border bg-muted/20 px-3 py-2 text-xs">
                  <div className="mb-1 font-medium">
                    Dateien ({groesse(b.filesBytes)}){b.filesAsLinkHint && ' — gehen je nach Versandweg als Link'}
                  </div>
                  {b.files.map((f) => (
                    <label key={f.projectFileId} className="flex cursor-pointer items-center gap-2 py-0.5">
                      <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-[hsl(var(--primary))]"
                        checked
                        onChange={() => setAbgehakt((alt) => new Set(alt).add(f.projectFileId))}
                      />
                      <span>{f.fileName}</span>
                      <span className="text-muted-foreground">
                        {groesse(f.sizeBytes)} · {f.line}
                      </span>
                      {f.hint === 'TYPE' && <span className="text-destructive">Dateityp wird nicht versendet</span>}
                      {f.hint === 'TOO_MANY' && <span className="text-destructive">mehr als 10 Dateien an einer Zeile</span>}
                    </label>
                  ))}
                </div>
              )}
            </article>
          ))}

          {withFiles && abgehakt.size > 0 && (
            <p className="text-xs text-muted-foreground">
              {mehrzahl(abgehakt.size, 'Datei', 'Dateien')} abgewählt —{' '}
              <button type="button" className="text-primary hover:underline" onClick={() => setAbgehakt(new Set())}>
                alle wieder mitsenden
              </button>
            </p>
          )}

          {zurueckgehalten.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-amber-300 bg-amber-50 text-sm dark:border-amber-700 dark:bg-amber-900/30">
              <div className="flex items-center gap-2 border-b border-amber-300/70 px-3 py-2 font-medium dark:border-amber-700/70">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                Zurückgehalten ({zurueckgehalten.length}) — diese Zeilen kommen in keinen Beleg
              </div>
              <ul className="divide-y divide-amber-300/50 dark:divide-amber-700/50">
                {zurueckgehalten.map((h, i) => (
                  <li key={h.lineId + i} className="px-3 py-1.5">
                    <div className="font-medium">{h.name}</div>
                    <div className="text-xs text-amber-800 dark:text-amber-200">{grundText(h.reason, art, h.detail)}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      </div>
    </Modal>
  )
}
